import { useMemo, useState } from "react";
import { requiredFlareHeightKosha, type KoshaFlareInput } from "../calc/koshaFlareRadiation";
import { mixFlareGasProperties, moleFractionsToMassComponents } from "../calc/gasComposition";
import { GAS_COMPONENTS } from "../data/gasComponents";
import { Field, Item, num } from "./formBits";
import { FlareGeometryDiagram } from "./FlareGeometryDiagram";

interface GasComponentEntry {
  readonly id: string;
  /** `gasComponents.ts` 의 성분 key. */
  readonly key: string;
  readonly molePercent: string;
}

interface FormState {
  compositionMode: "average" | "components";
  lelPrime: string;
  molecularWeightKgKmol: string;
  heatingValueKcalKg: string;
  components: readonly GasComponentEntry[];
  massFlowKgH: string;
  tipPressureKpaAbs: string;
  tipTempK: string;
  ambientTempK: string;
  windVelocityMs: string;
  airMolecularWeightKgKmol: string;
  compressibility: string;
  machNumber: string;
  transmissivity: string;
  allowableRadiationKcalHm2: string;
  backgroundRadiationKcalHm2: string;
  fractionHeatRadiated: string;
}

/** 초기값은 검증에 쓴 실제 프로젝트 사례("2.6m 시트", 순수 수소) 조건이다. */
const INITIAL: FormState = {
  compositionMode: "average",
  lelPrime: "0.04",
  molecularWeightKgKmol: "2.016",
  heatingValueKcalKg: "33891.05",
  components: [{ id: "bs_gc_0", key: "hydrogen", molePercent: "100" }],
  massFlowKgH: "10800",
  tipPressureKpaAbs: "106.3",
  tipTempK: "323",
  ambientTempK: "293",
  windVelocityMs: "2.6",
  airMolecularWeightKgKmol: "28.97",
  compressibility: "1",
  machNumber: "0.5",
  transmissivity: "1",
  allowableRadiationKcalHm2: "4000",
  backgroundRadiationKcalHm2: "860",
  fractionHeatRadiated: "0.3",
};

function fmt(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("ko-KR", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function GasCompositionEditor({
  entries,
  totalMassFlowKgH,
  onChange,
}: {
  entries: readonly GasComponentEntry[];
  totalMassFlowKgH: string;
  onChange: (entries: readonly GasComponentEntry[]) => void;
}) {
  const derived = useMemo(() => {
    const components = moleFractionsToMassComponents(
      num(totalMassFlowKgH),
      entries.map((e) => ({ key: e.key, molePercent: num(e.molePercent) })),
    );
    return components === null ? null : mixFlareGasProperties(components);
  }, [entries, totalMassFlowKgH]);

  const molePercentSum = entries.reduce((a, e) => a + (num(e.molePercent) || 0), 0);

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>성분</th>
              <th>몰%</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>
                  <select
                    value={e.key}
                    onChange={(ev) =>
                      onChange(entries.map((x) => (x.id === e.id ? { ...x, key: ev.target.value } : x)))
                    }
                  >
                    {GAS_COMPONENTS.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                        {c.lelVolPercent === null
                          ? c.nitrogenEquivalent == null
                            ? " (불연성, 계산 미지원)"
                            : " (불연성)"
                          : ""}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={e.molePercent}
                    onChange={(ev) =>
                      onChange(
                        entries.map((x) => (x.id === e.id ? { ...x, molePercent: ev.target.value } : x)),
                      )
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={entries.length <= 1}
                    onClick={() => onChange(entries.filter((x) => x.id !== e.id))}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() =>
          onChange([...entries, { id: `bs_gc_${Date.now()}_${entries.length}`, key: "nitrogen", molePercent: "0" }])
        }
      >
        + 성분 추가
      </button>
      <p className={Math.abs(molePercentSum - 100) > 0.5 ? "hint hint--warn" : "hint"}>
        몰% 합계: {fmt(molePercentSum, 1)}% {Math.abs(molePercentSum - 100) > 0.5 && "— 100%에 맞추십시오"}
      </p>
      {derived ? (
        <dl className="result__grid">
          <Item label="유도 분자량 Mj" value={`${fmt(derived.molecularWeight, 3)} kg/kmol`} />
          <Item label="유도 폭발하한계 CL'" value={fmt(derived.lelPrime, 4)} />
          <Item label="유도 연소열량(HHV)" value={`${fmt(derived.heatingValueKcalKg, 0)} kcal/kg`} />
        </dl>
      ) : (
        <p className="hint hint--warn">
          가연성 성분이 하나도 없거나 입력이 불완전합니다 — CL'·연소열량을 구하려면 가연성 성분(불연성
          아님)이 하나 이상 있어야 합니다. "계산 미지원" 불연성 성분(예: 염소)이 섞여 있으면 질소등가계수를
          몰라 CL' 보정을 할 수 없습니다.
        </p>
      )}
    </>
  );
}

export function KoshaFlareBsPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const flareGasMix = useMemo(() => {
    if (f.compositionMode !== "components") return null;
    const components = moleFractionsToMassComponents(
      num(f.massFlowKgH),
      f.components.map((e) => ({ key: e.key, molePercent: num(e.molePercent) })),
    );
    return components === null ? null : mixFlareGasProperties(components);
  }, [f.compositionMode, f.components, f.massFlowKgH]);

  const result = useMemo(() => {
    const usingComponents = f.compositionMode === "components";
    if (usingComponents && flareGasMix === null) {
      return {
        ok: false as const,
        reason: "invalid-input" as const,
        message: "가스 조성으로 CL'·분자량·연소열량을 구하지 못했습니다 — 성분·몰%를 확인하십시오.",
      };
    }
    const input: KoshaFlareInput = {
      lelPrime: usingComponents ? flareGasMix!.lelPrime : num(f.lelPrime),
      molecularWeightKgKmol: usingComponents ? flareGasMix!.molecularWeight : num(f.molecularWeightKgKmol),
      massFlowKgH: num(f.massFlowKgH),
      heatingValueKcalKg: usingComponents ? flareGasMix!.heatingValueKcalKg : num(f.heatingValueKcalKg),
      tipPressureKpaAbs: num(f.tipPressureKpaAbs),
      tipTempK: num(f.tipTempK),
      ambientTempK: num(f.ambientTempK),
      windVelocityMs: num(f.windVelocityMs),
      airMolecularWeightKgKmol: num(f.airMolecularWeightKgKmol),
      compressibility: num(f.compressibility),
      machNumber: num(f.machNumber),
      transmissivity: num(f.transmissivity),
      allowableRadiationKcalHm2: num(f.allowableRadiationKcalHm2),
      backgroundRadiationKcalHm2: num(f.backgroundRadiationKcalHm2),
      fractionHeatRadiated: num(f.fractionHeatRadiated),
    };
    return requiredFlareHeightKosha(input);
  }, [f, flareGasMix]);

  return (
    <section className="card">
      <h2>플레어 복사열·높이 (KOSHA, Brzustowski-Sommer 법)</h2>
      <p className="hint">
        KOSHA GUIDE C-C-18-2026(舊 D-59-2020) 「플레어시스템의 설계·설치 및
        운전에 관한 기술지침」이 요구하는 방법입니다. API 521 단순법(다른
        탭)과 달리 화염 중심 위치(yc·xc)를 djR(제트/바람 추력비)·CL(보정
        폭발하한계) 노모그래프(&lt;그림 5&gt;·&lt;그림 6&gt;)에서 구합니다.
      </p>
      <p className="hint hint--warn">
        ⚠ yc·xc 조회값은 그 노모그래프를 사용자가 직접 읽거나 디지털화한
        근사치입니다 — KOSHA 지침 원문·실제 프로젝트 검증 사례 대비 오차가
        대체로 10% 안팎이고, djR이 75~150 부근이면 최대 16%까지 커집니다
        (data/kosha-flare-bs.json accuracyNote 참고). 설계 여유가 크지
        않다면 반드시 원본 KOSHA 그림 &lt;그림 5&gt;·&lt;그림 6&gt;으로
        재확인하십시오. 초기값은 검증에 쓴 프로젝트 사례 조건입니다.
      </p>

      <h3>설계조건</h3>
      <div className="grid-form">
        <Field label="입력 방식" unit="" help="bsCompositionMode">
          <select
            value={f.compositionMode}
            onChange={(e) => set("compositionMode")(e.target.value as "average" | "components")}
          >
            <option value="average">CL'·분자량·연소열량 직접 입력</option>
            <option value="components">가스 조성(몰%)으로 입력</option>
          </select>
        </Field>
        <Field label="유량 W" unit="kg/h" help="bsMassFlow">
          <input value={f.massFlowKgH} onChange={(e) => set("massFlowKgH")(e.target.value)} />
        </Field>
      </div>

      {f.compositionMode === "components" ? (
        <GasCompositionEditor
          entries={f.components}
          totalMassFlowKgH={f.massFlowKgH}
          onChange={set("components")}
        />
      ) : (
        <div className="grid-form">
          <Field label="폭발하한계 CL'" unit="" help="bsLelPrime">
            <input value={f.lelPrime} onChange={(e) => set("lelPrime")(e.target.value)} />
          </Field>
          <Field label="분자량 Mj" unit="kg/kmol" help="bsMolecularWeight">
            <input
              value={f.molecularWeightKgKmol}
              onChange={(e) => set("molecularWeightKgKmol")(e.target.value)}
            />
          </Field>
          <Field label="연소열량(HHV)" unit="kcal/kg" help="bsHeatingValue">
            <input
              value={f.heatingValueKcalKg}
              onChange={(e) => set("heatingValueKcalKg")(e.target.value)}
            />
          </Field>
        </div>
      )}

      <div className="grid-form">
        <Field label="플레어가스 압력 Pj" unit="kPa abs" help="bsTipPressure">
          <input
            value={f.tipPressureKpaAbs}
            onChange={(e) => set("tipPressureKpaAbs")(e.target.value)}
          />
        </Field>
        <Field label="플레어가스 온도 Tj" unit="K" help="bsTipTemp">
          <input value={f.tipTempK} onChange={(e) => set("tipTempK")(e.target.value)} />
        </Field>
        <Field label="대기온도 Ta" unit="K" help="bsAmbientTemp">
          <input value={f.ambientTempK} onChange={(e) => set("ambientTempK")(e.target.value)} />
        </Field>
        <Field label="풍속 Uw" unit="m/s" help="bsWindVelocity">
          <input value={f.windVelocityMs} onChange={(e) => set("windVelocityMs")(e.target.value)} />
        </Field>
        <Field label="공기 분자량 Ma" unit="kg/kmol" help="bsAirMolecularWeight">
          <input
            value={f.airMolecularWeightKgKmol}
            onChange={(e) => set("airMolecularWeightKgKmol")(e.target.value)}
          />
        </Field>
        <Field label="압축인자 Z" unit="" help="bsCompressibility">
          <input value={f.compressibility} onChange={(e) => set("compressibility")(e.target.value)} />
        </Field>
        <Field label="마하수" unit="0.2~0.5" help="bsMachNumber">
          <input value={f.machNumber} onChange={(e) => set("machNumber")(e.target.value)} />
        </Field>
      </div>

      <h3>복사열 조건</h3>
      <div className="grid-form">
        <Field label="대기 투과율 τ" unit="0~1" help="bsTransmissivity">
          <input value={f.transmissivity} onChange={(e) => set("transmissivity")(e.target.value)} />
        </Field>
        <Field label="방출 복사열 비율 F" unit="0~1" help="bsFractionHeatRadiated">
          <input
            value={f.fractionHeatRadiated}
            onChange={(e) => set("fractionHeatRadiated")(e.target.value)}
          />
        </Field>
        <Field label="최대허용 복사열량 K" unit="kcal/hr·m²" help="bsAllowableRadiation">
          <input
            value={f.allowableRadiationKcalHm2}
            onChange={(e) => set("allowableRadiationKcalHm2")(e.target.value)}
          />
        </Field>
        <Field label="배경(태양) 복사량 K0" unit="kcal/hr·m²" help="bsBackgroundRadiation">
          <input
            value={f.backgroundRadiationKcalHm2}
            onChange={(e) => set("backgroundRadiationKcalHm2")(e.target.value)}
          />
        </Field>
      </div>

      {!result.ok ? (
        <div className="result result--error" role="alert">
          {result.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">최소 스택 높이 H</span>
            <strong className="result__value">{fmt(result.requiredHeightM, 1)}</strong>
            <span className="result__label">m</span>
          </div>

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="팁 지름 dj" value={`${fmt(result.jetDiameterM, 3)} m`} />
            <Item label="팁 방출속도 Uj" value={`${fmt(result.jetVelocityMs, 1)} m/s`} />
            <Item label="보정 폭발하한계 CL" value={fmt(result.correctedLel, 4)} />
            <Item label="제트/바람 추력비 djR" value={fmt(result.jetToWindThrustRatio, 2)} />
            <Item label="발열량 Q" value={`${fmt(result.heatReleaseKcalH, 0)} kcal/hr`} />
            <Item label="화염중심 수직거리 yc" value={`${fmt(result.ycM, 1)} m`} />
            <Item label="화염중심 수평거리 xc" value={`${fmt(result.xcM, 1)} m`} />
            <Item label="화염중심~대상지점 거리 D" value={`${fmt(result.radiationDistanceM, 1)} m`} />
          </dl>

          {result.warnings.length > 0 && (
            <ul className="warnings">
              {result.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          <h3>개략도</h3>
          <FlareGeometryDiagram
            heightM={result.requiredHeightM}
            horizontalDisplacementM={result.xcM}
            verticalDisplacementM={result.ycM}
            centerOffsetIsDirect
            radiationDistanceM={result.radiationDistanceM}
            gradeDistanceM={result.xcM}
            windVelocityMs={num(f.windVelocityMs)}
            allowableRadiationKwM2={num(f.allowableRadiationKcalHm2)}
            allowableRadiationUnit="kcal/hr·m²"
            offsetLabel={`xc=${fmt(result.xcM, 1)} m · yc=${fmt(result.ycM, 1)} m`}
          />
        </div>
      )}
    </section>
  );
}
